import { FiBook } from 'react-icons/fi';

export const awlSublist7Data = {
  courseId: 'awl-sublist-7',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 7',
  icon: FiBook,
  overview: {
    impact: 'Control argument and research-process words that appear in Band 7+ Reading and thesis-led essays',
    focus: 'confirm, infer, comprehensive — plus empirical, phenomenon, paradigm, priority',
    uses: 'Reading on science and social research; Writing Task 2 when you confirm a view, infer a cause, or set a priority',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Sublist 7 is the research-and-argument layer of the AWL. Passages about experiments, ideology, and policy trials use these words to describe how knowledge is built: you <em>infer</em> from data, <em>confirm</em> or <em>deny</em> a claim, <em>isolate</em> a variable, and sometimes <em>simulate</em> a process. In Task 2, the same verbs let you talk about evidence instead of repeating “I think.”</p>
<p>The full Sublist 7 headword set in this course is: adapt, advocate, channel, comprehensive, comprise, confirm, contrary, convert, decade, definite, deny, differentiate, dispose, dynamic, eliminate, empirical, equip, extract, finite, foundation, guarantee, hierarchy, identical, ideology, infer, innovate, intervene, isolate, paradigm, phenomenon, priority, prohibit, publication, quote, reverse, simulate, submit, survive, thesis, unique.</p>
<p>Start with <strong>confirm</strong>, <strong>infer</strong>, and <strong>comprehensive</strong>. Confirm is what results do to a hypothesis. Infer is what a reader does when the writer does not state the point directly — which is the skill behind many True/False/Not Given items. Comprehensive means complete in coverage, a useful adjective for reforms and reviews.</p>`,
      points: [
        'Empirical and phenomenon are high-value in science-style Reading.',
        'Advocate and priority belong in opinion essays about what governments should do first.',
        'Comprise means “be made up of”; it is not followed by “of” in careful academic style.',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['advocate', 'To publicly support; a person who supports a cause', 'Many doctors advocate a sugar tax to reduce childhood obesity.'],
          ['comprehensive', 'Including all or nearly all elements', 'The review was comprehensive and covered a full decade of data.'],
          ['confirm', 'To show that something is true or definite', 'The results confirm the original hypothesis.'],
          ['empirical', 'Based on observation or experiment rather than theory alone', 'There is limited empirical evidence that longer sentences reduce crime.'],
          ['infer', 'To reach a conclusion from evidence, not from a direct statement', 'From the decline in applications we can infer that fees are a barrier.'],
          ['intervene', 'To become involved in order to change a situation', 'The central bank intervened to steady the currency.'],
          ['isolate', 'To separate from others; to identify as distinct', 'Researchers isolated one variable: class size.'],
          ['paradigm', 'A typical example or a model of how things are done', 'Remote assessment represents a new paradigm in university testing.'],
          ['phenomenon', 'A fact or event that can be observed, often unusual', 'Urban heat islands are a well-documented phenomenon.'],
          ['priority', 'Something treated as more important than other things', 'Clean water should be a higher priority than decorative projects.'],
          ['thesis', 'A long research argument; a main claim', 'Her thesis is that finite resources cannot support unlimited growth.'],
          ['unique', 'Being the only one of its kind', 'Each city’s transport problem is unique, so copied policies often fail.'],
        ],
      },
    },
    {
      title: 'Word families',
      content: `<p>Research nouns are the prize here. <em>Confirm</em> → <em>confirmation</em>. <em>Infer</em> → <em>inference</em>. <em>Innovate</em> → <em>innovation</em> / <em>innovative</em>. <em>Publish</em> is not on the list, but <em>publication</em> is — a reminder that AWL headwords are not always the verb you expect.</p>
<p>Watch Latin plurals that appear in Reading: <em>phenomenon</em> / <em>phenomena</em>. <em>Thesis</em> / <em>theses</em>. Using the plural as if it were singular (“this phenomena is”) is a noticeable error. <em>Comprehensive</em> has no everyday verb; do not invent “comprehensivise.” Use <em>cover</em> or <em>review comprehensively</em> instead.</p>`,
      points: [
        'Confirm / confirmation / confirmed — results confirm; we seek confirmation.',
        'Infer is the verb; inference is the noun. Do not write “an infer.”',
        'Advocate is verb and noun; advocacy is the related noun for the activity.',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['advocate', 'advocate, advocacy', 'advocate', '—'],
          ['comprehensive', 'comprehensiveness', '—', 'comprehensive, comprehensively'],
          ['confirm', 'confirmation', 'confirm', 'confirmed, confirmatory'],
          ['empirical', 'empiricism', '—', 'empirical, empirically'],
          ['infer', 'inference', 'infer', 'inferential'],
          ['intervene', 'intervention', 'intervene', 'intervening'],
          ['isolate', 'isolation', 'isolate', 'isolated, isolating'],
          ['paradigm', 'paradigm', '—', 'paradigmatic'],
          ['phenomenon', 'phenomenon, phenomena', '—', 'phenomenal'],
          ['priority', 'priority, prioritisation', 'prioritise', 'prior'],
          ['unique', 'uniqueness', '—', 'unique, uniquely'],
          ['innovate', 'innovation, innovator', 'innovate', 'innovative'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>These partnerships appear in journals and in IELTS source texts. “Confirm a hypothesis,” “draw an inference,” “empirical evidence,” “a unique phenomenon,” and “set a priority” will serve you in both papers. “Do a confirm” will not.</p>
<p>Argument verbs have their own company: <em>contrary to</em>, <em>deny a claim</em>, <em>differentiate between</em>, <em>prohibit X from -ing</em>. Process verbs: <em>adapt to</em>, <em>convert into</em>, <em>extract from</em>, <em>submit a proposal</em>. Learn <em>guarantee</em> as both noun and verb, but avoid absolute guarantees in essays unless you can support them.</p>`,
      points: [
        'Confirm collocates with hypothesis, finding, and suspicion.',
        'Infer collocates with from + evidence: we infer X from Y.',
      ],
      examples: [
        { label: 'confirm', text: 'confirm a hypothesis, confirm the findings, independently confirmed' },
        { label: 'infer', text: 'infer from the data, reasonable to infer, draw an inference' },
        { label: 'comprehensive', text: 'comprehensive review, comprehensive reform, comprehensive coverage' },
        { label: 'empirical', text: 'empirical evidence, empirical study, empirically tested' },
        { label: 'phenomenon', text: 'a global phenomenon, explain the phenomenon, observed phenomenon' },
        { label: 'paradigm', text: 'a new paradigm, paradigm shift, dominant paradigm' },
        { label: 'priority', text: 'a top priority, set priorities, priority over' },
        { label: 'advocate', text: 'advocate a policy, advocate for, strong advocate of' },
        { label: 'intervene', text: 'intervene in, government intervention, early intervention' },
        { label: 'unique', text: 'unique to, unique challenge, unique opportunity' },
      ],
    },
    {
      title: 'Using Sublist 7 in IELTS writing and reading',
      content: `<p>In Reading, when a question asks what the writer suggests but does not say, the skill is <em>inference</em>. In Writing, say “the results confirm…” only if you then give a concrete illustration; invented statistics still look invented even with an AWL verb. Prefer “surveys tend to confirm that…” or a clearly hypothetical example.</p>
<p>Thesis statements can use <em>priority</em> and <em>advocate</em>: “I would advocate treating housing as the first priority, because other reforms cannot survive if workers cannot live near jobs.” <em>Comprehensive</em> is a good adjective for solutions, but only if you then mention more than one part — otherwise the word is empty.</p>`,
      points: [
        'Use infer for conclusions that are not stated word for word.',
        'Use confirm when later evidence supports an earlier claim.',
        'Identical is stronger than similar; unique is stronger than unusual — do not overuse them.',
      ],
      examples: [
        { label: 'Weak sentence', text: 'The results say the first idea was right.' },
        { label: 'Strong sentence', text: 'The results confirm the original hypothesis.' },
        { label: 'Weak sentence', text: 'We can guess fees are the problem.' },
        { label: 'Strong sentence', text: 'From the decline in applications we can infer that fees are a barrier.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “confirm” mean?',
          options: ['To show that something is true or definite', 'To invent a new claim', 'To delay a decision forever'],
          answer: 'To show that something is true or definite',
          explanation: 'Confirm means later evidence supports an earlier idea. It is the opposite of deny or contradict.',
        },
        {
          text: 'What does “infer” mean?',
          options: ['To copy a sentence word for word', 'To reach a conclusion from evidence that is not stated directly', 'To refuse to read the data'],
          answer: 'To reach a conclusion from evidence that is not stated directly',
          explanation: 'Inference is reading between the lines — the skill behind many IELTS Reading items.',
        },
        {
          text: 'What does “comprehensive” mean?',
          options: ['Including all or nearly all elements', 'Extremely short and incomplete', 'Related only to comprehension tests'],
          answer: 'Including all or nearly all elements',
          explanation: 'Comprehensive describes coverage. A comprehensive review leaves few parts out. It is not the same word as “comprehension.”',
        },
        {
          text: 'What does “empirical” mean?',
          options: ['Based on observation or experiment', 'Based only on tradition', 'Imaginary and untested'],
          answer: 'Based on observation or experiment',
          explanation: 'Empirical evidence comes from the world — surveys, trials, measurements — not from theory alone.',
        },
        {
          text: 'What does “phenomenon” mean?',
          options: ['A fact or event that can be observed', 'A type of government minister', 'A spelling rule'],
          answer: 'A fact or event that can be observed',
          explanation: 'A phenomenon is an observable event or situation, often one that needs explanation. The plural is phenomena.',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 7 word.',
      questions: [
        {
          text: 'The results ____ the original hypothesis.',
          answer: 'confirm',
          explanation: 'Results confirm a hypothesis. After a plural subject in the present simple, use confirm, not confirmation.',
        },
        {
          text: 'From the empty shops we can ____ that the high street is under pressure.',
          answer: 'infer',
          explanation: 'Infer is the verb for a conclusion drawn from signs rather than a direct statement.',
        },
        {
          text: 'Housing should be the government’s first ____ this decade.',
          answer: 'priority',
          explanation: 'Priority is the noun for what comes first. “First priority” is a standard collocation.',
        },
        {
          text: 'The report was ____: it covered funding, staffing, and outcomes.',
          answer: 'comprehensive',
          explanation: 'Comprehensive fits a review that includes all major parts. The list after the colon shows the coverage.',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'The results ____ the original hypothesis. (confirm)',
          options: ['confirm', 'confirmation', 'confirmed'],
          answer: 'confirm',
          explanation: 'A present plural verb is required. Confirmation is a noun; confirmed would need an auxiliary (“have confirmed”) or a different tense story.',
        },
        {
          text: 'That ____ is not safe; the passage never states the cause. (infer)',
          options: ['infer', 'inference', 'inferior'],
          answer: 'inference',
          explanation: 'After “that” you need a noun. Inference is the conclusion. Infer is a verb; inferior is a different word meaning lower in quality.',
        },
        {
          text: 'Early ____ can prevent a small problem becoming a crisis. (intervene)',
          options: ['intervene', 'intervention', 'intervened'],
          answer: 'intervention',
          explanation: 'After the adjective “early” you need a noun. Intervention is the academic noun for stepping in.',
        },
        {
          text: 'These two policies are not ____; one is a tax and the other is a ban. (identical)',
          options: ['identical', 'identity', 'identically'],
          answer: 'identical',
          explanation: 'After “are not” you need an adjective. Identity is a noun; identically is an adverb.',
        },
      ],
    },
  ],
  tips: [
    'Confirm is for evidence that supports a claim; infer is for a conclusion the text does not spell out.',
    'Learn phenomenon / phenomena — mixing the plural is a visible error in Task 2.',
    'Use comprehensive only when you then mention more than one part of the solution.',
    'Comprise already includes “of” in its meaning: “The committee comprises five experts.”',
  ],
  dailyPractice: [
    'Write a two-sentence paragraph that confirms one claim and infers a second from a sign.',
    'Read a science paragraph and mark empirical, phenomenon, or paradigm if they appear.',
    'Turn “I think housing is most important” into a sentence with advocate and priority.',
    'List the noun forms of confirm, infer, intervene, isolate, and innovate from memory.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 7 vocabulary course covering Coxhead headwords such as advocate, comprehensive, confirm, empirical, infer, intervene, isolate, paradigm, phenomenon, priority, thesis, and unique. Includes the full stub list, word families, collocations, and IELTS uses for research process and argument.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist7Data,
      title: awlSublist7Data.title + ' (Foundations)',
      overview: { ...awlSublist7Data.overview, duration: '15-20 minutes' },
      sections: awlSublist7Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist7Data.sections.length / 2))),
      exercises: awlSublist7Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist7Data,
      title: awlSublist7Data.title + ' (Advanced)',
      overview: { ...awlSublist7Data.overview, duration: '30-35 minutes' },
      sections: awlSublist7Data.sections,
      exercises: awlSublist7Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist7Data.tips || []),
        'For Band 8+: use paradigm shift only when the model of work truly changes, not for any small reform.',
        'Advanced tip: contrast empirical evidence with ideology in one evaluated sentence.',
      ],
    };
  }
  return awlSublist7Data;
}
